import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map } from "maplibre-gl";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

import { useAdminAreaById } from "@/api/adminArea/userAdminAreaAPI";
import { GLOBAL_ADMIN_SOURCE_ID } from "@/constants/map";
import { getValueOfHighestLevelAdminArea } from "@/helpers/datasetAttributeHelper";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setAdminAreaSettingsData } from "@/store/slice/globalFilterSlice";
import { AdministrativeArea } from "@/types/adminArea";
import {
  addPolygonOutlineLayer,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";
import { addPolygonCatchmentSource } from "@/utils/map/polygon";

type UseGlobalAdminAreaProps = {
  map: Map;
  isMapLoaded: boolean;
  zoomLevel: number;
};

export const useGlobalAdminArea = ({
  map,
  isMapLoaded,
  zoomLevel
}: UseGlobalAdminAreaProps) => {
  const { isGlobalFilterEnabled } = useAppFeatures();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const {
    adminAreaSettingsData,
    adminAreaSettingsDataTemp,
    adminAreaSettings
  } = useSelector((state: RootState) => state.globalFilter);

  const ui = useSelector((state: RootState) => state.ui);
  const [adminAreaId, setAdminAreaId] = useState<string | null>(null);

  const isPreviewMode = searchParams.get("preview") === "true";

  // ------------------------ Fetch admin area by ID ------------------------
  const {
    data: adminAreaDataById = null,
    isLoading,
    isError
  } = useAdminAreaById(adminAreaId, zoomLevel, {
    enabled: !ui.showGlobalFilterPopup && isGlobalFilterEnabled
  });

  useEffect(() => {
    if (!isLoading && !isError) {
      dispatch(setAdminAreaSettingsData(adminAreaDataById));
    }
  }, [adminAreaDataById, dispatch, isLoading, isError]);

  // ------------------------ Helpers ------------------------
  const extractBoundaries = (collection?: AdministrativeArea[]) =>
    collection?.flatMap((item) => item.boundary || []) || [];

  const addAdminAreaLayers = (
    data: FeatureCollection<Geometry, GeoJsonProperties>[]
  ) => {
    removeAllSourceLayerWithPrefix(map, GLOBAL_ADMIN_SOURCE_ID, []);
    data.forEach((item, index) => {
      const id = `${GLOBAL_ADMIN_SOURCE_ID}-${index}`;
      if (!map.getSource(id)) {
        addPolygonCatchmentSource(map, id, item);
        addPolygonOutlineLayer(map, id);
      }
    });
  };

  const shouldResetAdminArea =
    adminAreaSettings === null ||
    ui.showGlobalFilterPopup ||
    ui.showAnalysisResultPopup ||
    ui.showComparisonPopup.isShow ||
    ui.showAnalysisSummaryPopup ||
    ui.showSearchLocationPopup ||
    isPreviewMode;

  // ------------------------ Determine Highest Admin Area ID ------------------------
  useEffect(() => {
    if (shouldResetAdminArea) {
      if (adminAreaId) setAdminAreaId(null);
      return;
    }

    const highest = getValueOfHighestLevelAdminArea(adminAreaSettings);
    if (highest) setAdminAreaId(highest);
  }, [
    zoomLevel,
    adminAreaSettings,
    ui,
    isPreviewMode,
    shouldResetAdminArea,
    adminAreaId
  ]);

  // ------------------------ Render Main & Temp Layer ------------------------
  useEffect(() => {
    if (!isMapLoaded) return;

    const sourceData = ui.showGlobalFilterPopup
      ? adminAreaSettingsDataTemp
      : adminAreaSettingsData;

    const boundaries = extractBoundaries(sourceData?.data);
    addAdminAreaLayers(boundaries);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    ui.showGlobalFilterPopup,
    adminAreaSettingsData,
    adminAreaSettingsDataTemp
  ]);

  return {};
};

export default useGlobalAdminArea;
